/**
 * Fan chart for a country's inflation forecast.
 *
 *   - 68% (p16/p84) + 90% (p5/p95) confidence bands in red.
 *   - Black `Historical` line (actual) connected to red `Forecast` (p50).
 *   - Dashed gray `One Month Ago Forecast` line.
 *   - Blue dot `Central Bank Forecast` markers.
 *   - NBER recession shading.
 */
import { useMemo } from 'react';
import type { Data, Layout } from 'plotly.js';
import PlotlyChart from '@/lib/plotly/PlotlyChart';
import { getRecessionShapes } from '@/lib/plotly/recessionShapes';
import { hexToRgba } from '@/lib/plotly/colors';
import { hoverTemplate } from '@/lib/plotly/hoverTemplates';
import { autoRangeYAxisMulti } from '@/lib/plotly/yAxisRange';
import { chartLayout } from '@/lib/plotly/layoutPresets';
import { AXIS_LINE } from '@/lib/plotly/defaults';
import { longName } from '../lib/countries';
import { processInflationData } from '../lib/processInflation';
import type { CountryIso, InflationRow, InflationType } from '../types';

export interface InflationChartProps {
  country: CountryIso;
  inflationType: InflationType;
  rows: InflationRow[];
}

// Confidence-band colors (red palette).
const OUTER_BAND_HEX = '#FEE2E2';
const INNER_BAND_HEX = '#FCA5A5';
const TRANSPARENT = 'rgba(0,0,0,0)';

/** Pair each date with its value, dropping null / non-finite entries. */
function definedPoints(
  dates: string[],
  values: (number | null)[],
): { x: string[]; y: number[] } {
  const x: string[] = [];
  const y: number[] = [];
  for (let i = 0; i < dates.length; i += 1) {
    const v = values[i];
    if (v !== null && Number.isFinite(v)) {
      x.push(dates[i]);
      y.push(v);
    }
  }
  return { x, y };
}

export default function InflationChart({
  country,
  inflationType,
  rows,
}: InflationChartProps): JSX.Element {
  const processed = useMemo(() => processInflationData(rows), [rows]);

  const { traces, layout } = useMemo(() => {
    if (processed.dates.length === 0) {
      return { traces: [] as Data[], layout: {} as Partial<Layout> };
    }

    const { dates, p5, p16, p50, p84, p95, actual, one_month, cb } = processed;

    // Confidence bands (red palette).
    const outerLower: Data = {
      x: dates,
      y: p5,
      mode: 'lines',
      line: { color: TRANSPARENT },
      hoverinfo: 'skip',
      showlegend: false,
      name: '90% Confidence (lower)',
      type: 'scatter',
    };
    const outerUpper: Data = {
      x: dates,
      y: p95,
      mode: 'lines',
      fill: 'tonexty',
      fillcolor: hexToRgba(OUTER_BAND_HEX, 0.36),
      line: { color: TRANSPARENT },
      hoverinfo: 'skip',
      name: '90% Confidence',
      type: 'scatter',
    };
    const innerLower: Data = {
      x: dates,
      y: p16,
      mode: 'lines',
      line: { color: TRANSPARENT },
      hoverinfo: 'skip',
      showlegend: false,
      name: '68% Confidence (lower)',
      type: 'scatter',
    };
    const innerUpper: Data = {
      x: dates,
      y: p84,
      mode: 'lines',
      fill: 'tonexty',
      fillcolor: hexToRgba(INNER_BAND_HEX, 0.6),
      line: { color: TRANSPARENT },
      hoverinfo: 'skip',
      name: '68% Confidence',
      type: 'scatter',
    };

    // Split into Historical (actual line) and Forecast (p50 where actual is
    // missing). Then bridge with the last historical point so the lines join
    // visually.
    const histX: string[] = [];
    const histY: number[] = [];
    const fcastX: string[] = [];
    const fcastY: number[] = [];
    for (let i = 0; i < dates.length; i += 1) {
      const a = actual[i];
      const f = p50[i];
      if (a !== null && Number.isFinite(a)) {
        histX.push(dates[i]);
        histY.push(a);
      } else if (f !== null && Number.isFinite(f)) {
        fcastX.push(dates[i]);
        fcastY.push(f);
      }
    }
    if (histX.length > 0 && fcastX.length > 0) {
      const lastHistDate = histX[histX.length - 1];
      const lastHistVal = histY[histY.length - 1];
      const firstFcast = new Date(fcastX[0]);
      const lastHist = new Date(lastHistDate);
      if (firstFcast >= lastHist) {
        fcastX.unshift(lastHistDate);
        fcastY.unshift(lastHistVal);
      }
    }

    const historicalTrace: Data | null =
      histX.length > 0
        ? {
            x: histX,
            y: histY,
            mode: 'lines',
            name: 'Historical',
            line: { color: '#000000', width: 2.5 },
            hovertemplate: hoverTemplate({ name: 'Historical', format: '.1f', suffix: '%' }),
            type: 'scatter',
          }
        : null;
    const forecastTrace: Data | null =
      fcastX.length > 0
        ? {
            x: fcastX,
            y: fcastY,
            mode: 'lines',
            name: 'Forecast',
            line: { color: '#DC2626', width: 2.5 },
            hovertemplate: hoverTemplate({ name: 'Forecast', format: '.1f', suffix: '%' }),
            type: 'scatter',
          }
        : null;

    // Optional one-month-ago dashed line.
    const prev = definedPoints(dates, one_month);
    const prevTrace: Data | null =
      prev.x.length > 0
        ? {
            x: prev.x,
            y: prev.y,
            mode: 'lines',
            name: 'One Month Ago Forecast',
            line: { color: '#6B7280', width: 2, dash: 'dash' },
            hovertemplate: hoverTemplate({ name: true, format: '.1f', suffix: '%' }),
            type: 'scatter',
          }
        : null;

    // Optional central-bank forecast dots.
    const cbPoints = definedPoints(dates, cb);
    const cbTrace: Data | null =
      cbPoints.x.length > 0
        ? {
            x: cbPoints.x,
            y: cbPoints.y,
            mode: 'markers',
            name: 'Central Bank Forecast',
            marker: { color: '#2563EB', size: 6, symbol: 'circle' },
            hovertemplate: hoverTemplate({ name: true, format: '.1f', suffix: '%' }),
            type: 'scatter',
          }
        : null;

    // Plotly renders traces in array order: first = bottom, last = top.
    // Correct z-order: confidence bands (bottom) → lines → dots (top).
    const assembled: Data[] = [];
    assembled.push(outerLower, outerUpper, innerLower, innerUpper);
    if (prevTrace) assembled.push(prevTrace);
    if (historicalTrace) assembled.push(historicalTrace);
    if (forecastTrace) assembled.push(forecastTrace);
    if (cbTrace) assembled.push(cbTrace);

    // y-axis range: gather all visible values, pad ~10%, enforce minimum 0.6%
    // total range so flat data still renders with breathing room. The NaN
    // fallback signals "no data" — the `Number.isFinite` check below catches it.
    const [rawMin, rawMax] = autoRangeYAxisMulti(
      [p50, actual, one_month, cb],
      dates,
      { paddingPct: 0, fallback: [Number.NaN, Number.NaN] },
    );
    let yMin = -0.3;
    let yMax = 0.3;
    if (Number.isFinite(rawMin) && Number.isFinite(rawMax)) {
      const range = rawMax - rawMin;
      const padding = Math.max(range * 0.1, 0.5);
      const proposedMin = rawMin - padding;
      const proposedMax = rawMax + padding;
      if (proposedMax - proposedMin < 0.6) {
        const center = (proposedMin + proposedMax) / 2;
        yMin = center - 0.3;
        yMax = center + 0.3;
      } else {
        yMin = proposedMin;
        yMax = proposedMax;
      }
    }

    // `dates` is sorted ascending by `processInflationData`.
    const minDate = new Date(dates[0]);
    const maxDate = new Date(dates[dates.length - 1]);

    const titleText = `${longName(country)} - ${inflationType} Inflation (% YoY)`;

    // Apply repo-wide chart convention: title centered at top, horizontal
    // legend BETWEEN title and chart drawing area, inner ticks. Title lives
    // in Plotly (no DOM h3 wrapper), so do not add `<ChartCard title>` for
    // this chart.
    const chartConv = chartLayout({ family: 'timeseries', title: titleText });
    const composedLayout: Partial<Layout> = {
      ...chartConv,
      // Legacy `inflation-forecasts.js` renders this chart's title at 16 and
      // legend at 12; the shared preset now defaults to 18 / 16, so pin both.
      title: { ...(chartConv.title as object), font: { size: 16, color: 'black' } },
      legend: {
        ...(chartConv.legend as object),
        font: { ...((chartConv.legend as { font?: object })?.font ?? {}), size: 12 },
      },
      xaxis: {
        type: 'date',
        tickformat: '%b-%y',
        hoverformat: '%b %Y',
        range: [minDate.toISOString(), maxDate.toISOString()],
        showgrid: true,
        gridcolor: '#DCDCDC',
        gridwidth: 1,
        ...chartConv.xaxis,
      },
      yaxis: {
        ...chartConv.yaxis,
        title: {
          text: 'Inflation Rate (%)',
          font: { size: 14, color: '#374151' },
          standoff: 10,
        },
        range: [yMin, yMax],
        tickformat: '.1f',
        showgrid: true,
        gridcolor: '#DCDCDC',
        gridwidth: 1,
      },
      yaxis2: {
        overlaying: 'y',
        side: 'right',
        showgrid: false,
        range: [yMin, yMax],
        tickformat: '.1f',
        title: { text: '' },
        ...AXIS_LINE,
        fixedrange: true,
      },
      shapes: getRecessionShapes({
        plotXRange: [minDate.toISOString(), maxDate.toISOString()],
      }),
    };

    return { traces: assembled, layout: composedLayout };
  }, [processed, country, inflationType]);

  if (processed.dates.length === 0) {
    return (
      <div className="flex h-64 items-center justify-center text-sm text-[color:var(--text-dark)] opacity-70">
        No inflation forecast data available
      </div>
    );
  }

  return (
    // 550 matches the 550px wrapper in EconomicOutlookPage.
    <div className="w-full" style={{ height: 550 }}>
      <PlotlyChart data={traces} layout={layout} />
    </div>
  );
}
